import React from "react";
import PropTypes from "prop-types";
import { Card, List, Typography, Progress, Button, Empty, Tooltip, Space } from "antd";
import { BookOutlined, ClockCircleOutlined } from "@ant-design/icons";
import { formatDateOnly } from "Common/Components/FormatDateTime";
import { useNavigate } from "react-router-dom";
import { useContext } from "react";
import { ProfileContext } from "Common/hooks/context/ProfileContext";
const { Text } = Typography;
const CourseList = ({ darkMode, enrolledCourses }) => {
  const { isProfileComplete } = useContext(ProfileContext);
  const isBlocked = !isProfileComplete;
  const navigate = useNavigate();
  const thumbnail = "https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&q=80";
  const styles = {
    card: {
      borderRadius: 6,
      backgroundColor: darkMode ? "#2a2a2a" : "#fff",
      borderColor: darkMode ? "#404040" : "#f0f0f0",
      cursor: isBlocked ? "not-allowed" : "pointer",
      padding: '12px',
      marginBottom: '8px'
    },
    textColor: {
      color: darkMode ? "rgba(255, 255, 255, 0.85)" : undefined,
    },
    secondaryTextColor: {
      color: darkMode ? "rgba(255, 255, 255, 0.45)" : "rgba(0, 0, 0, 0.45)",
    },
  };
  const isEmpty = !enrolledCourses || enrolledCourses.length === 0;
  return (
    <Tooltip title={isBlocked ? "Complete your profile to view stats" : ""}>
      <Card
        title={
          <Text strong style={styles.textColor}>
            <BookOutlined style={{ marginRight: 8 }} /> My Courses
          </Text>
        }
        style={{
          borderRadius: 8,
          backgroundColor: darkMode ? "#1f1f1f" : "#fff",
          borderColor: darkMode ? "#303030" : "#f0f0f0",
        }}
        bodyStyle={{ padding: '12px' }}
        extra={
          !isEmpty && (
            <Button
              type="link"
              size="small"
              onClick={() => !isBlocked && navigate("/all-courses")}
              disabled={isBlocked}
            >
              View All
            </Button>
          )
        }
      >
        {isEmpty ? (
          <Empty description={<Text type="secondary">No active courses</Text>} />
        ) : (
          <List
            itemLayout="vertical"
            dataSource={enrolledCourses.slice(0, 3)}
            renderItem={(course) => (
              <List.Item style={{ padding: '4px 0' }}>
                <Card hoverable style={styles.card}>
                  <Space align="start" size={12} style={{ width: '100%' }}>
                    <img
                      src={thumbnail}
                      alt={course.title}
                      style={{
                        width: '80px',
                        borderRadius: 4,
                        aspectRatio: "4/3",
                        objectFit: "cover",
                        flexShrink: 0
                      }}
                    />
                    <div style={{ flex: 1 }}>
                      <Text strong style={{ ...styles.textColor, display: 'block', marginBottom: 2 }}>
                        {course.title}
                      </Text>
                      <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: 8 }}>
                        Instructor: {course.mentorName}
                      </Text>
                      <Progress
                        percent={course.progress}
                        strokeColor={darkMode ? "#1890ff" : "#40a9ff"}
                        showInfo={false}
                        size="small"
                      />
                      <Space size={8} style={{ marginTop: 4 }}>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          {course.progress}% complete
                        </Text>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          <ClockCircleOutlined /> {formatDateOnly(course.endDate)}
                        </Text>
                      </Space>
                    </div>
                  </Space>
                </Card>
              </List.Item>
            )}
          />
        )}
      </Card>
    </Tooltip>
  );
};
CourseList.propTypes = {
  darkMode: PropTypes.bool.isRequired,
  enrolledCourses: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      title: PropTypes.string.isRequired,
      mentorName: PropTypes.string.isRequired,
      progress: PropTypes.number.isRequired,
      endDate: PropTypes.string.isRequired,
    })
  ),
};
export default CourseList;
